import { useEffect } from 'react';
import { selectUser, setLastRefresh, updateUser } from '../redux/authSlice';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fnReadUserPrefs } from '../sputnikapi';
import { useConsole } from '../../components/common/console/Console';
import { AuthedUser } from '../../types/types';
import { SputnikAPIBaseURL } from '../customShared';
import { AJAXSettings, getJwtToken, setJwtToken } from '../shared';
import { fnAJAXFetch } from '../helpers';

async function fetchUserAttributesIfNeeded(userID: number, dispatch: ReturnType<typeof useAppDispatch>, fnConsoleError: ReturnType<typeof useConsole>['fnConsoleError']): Promise<void> {
    dispatch(setLastRefresh());

    await fnReadUserPrefs(userID, '$', dispatch)
        .then((result) => {
        if (result.success) {
          // console.log(`setting keyaddress: 'userattributes', keyvalue: ${result?.data?.content?.data}`)
            dispatch(updateUser({ keyaddress: 'userattributes', keyvalue: result?.data?.content?.data }));
        } else {
            fnConsoleError(dispatch, "Unable to fnReadUserPrefs", result.data);
        }
        })
        .catch((error) => {
            fnConsoleError(dispatch, error);
        });
}

export const useUserWithAttributes = (): AuthedUser | null => {
  const user = useAppSelector(selectUser);
  // console.log(user)
  const dispatch = useAppDispatch();
  const { fnConsoleError } = useConsole();

  useEffect(() => {
    if (user && (user.refresh || 0) < new Date().getTime()) {
        // console.log('updating user attributes as: ',`(${user} && (!${user?.userattributes} || (${user.refresh} || 0) < ${new Date().getTime()}))`)
      fetchUserAttributesIfNeeded(user.userid, dispatch, fnConsoleError);
    }
  }, [user, dispatch, fnConsoleError]);

  return user;
};


/**
 * Authenticates a user using their username and password.
 *
 * This function makes an HTTP POST request to the /auth endpoint to authenticate
 * the user. If the request is successful, it will call the provided callback
 * function with the response data. If an error occurs during the request,
 * it will also call the callback function with information about the error.
 *
 * @param username - The user's username.
 * @param password - The user's password.
 * @param outcomeCallback - A callback function to be called when the request completes.
 *                          The callback function should take an object with properties
 *                          ajax_success (a boolean indicating whether the request was successful),
 *                          ajax_value (the response data, if any),
 *                          ajax_status (the HTTP status code), and
 *                          ajax_headers (the response headers).
 * @returns A Promise that resolves when the request completes. Note that this
 *          function does not itself reject the Promise if the request fails; 
 *          instead, it calls the callback function with error information.
 */
export async function authenticateUser(
  username: string,
  password: string,
  outcomeCallback: ((success: boolean, data: any) => void),
  dispatch:any
): Promise<void> {
  const { fnConsoleError } = useConsole()

  try {
    var auth = window.btoa(username + ":" + password)
    var tAJAXSettings: AJAXSettings = {
      method: "POST",
      url: `${SputnikAPIBaseURL}auth`,
      returnContentType: "json",
      authorization: `Basic ${auth}`,
      data: {},
      mode: "cors",
      credentials: "include",
      outcome_callback: function (success: boolean, status: number, value: any, headers: any ) {
        try {
          // console.log(document.cookie);
          outcomeCallback( success, value)
        } catch (err) {
          fnConsoleError(dispatch,err)
        };
      },
      dispatch:dispatch
    }
    await fnAJAXFetch(tAJAXSettings)
    // await fnAJAXFetch(tAJAXSettings,dispatch)
  } catch (error) {
    fnConsoleError(dispatch,'there was an error in cb')
    if (error instanceof Error) {
      outcomeCallback(false, {error: `${error.name}: ${error.message}`})
    }
  }
};



/**
 * Refreshes the session against Atoll's /auth/refresh (Sputnik's own
 * /renew was retired in the Atoll cutover -- see sputnik/asgi/main.py's
 * module docstring). client_id is pulled from the current access token's
 * own `aud` claim rather than passed in, since this shared module has no
 * per-app Atoll config (that lives in each frontend's local
 * shared/atoll/init.ts, and differs between orbotnik and tycho).
 */
export async function renewUserSession(
    dispatch:any
  ): Promise<{
    success: boolean; status: number; value: {
      error?: any; content?: any; finalstate?: {}; description?:string; outcome?: any
    }; headers: any;
}> {
  const accessToken = getJwtToken();
  const refreshToken = sessionStorage.getItem('refresh_token') || '';

  let clientId = '';
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    clientId = Array.isArray(payload.aud) ? payload.aud[0] : payload.aud;
  } catch {}

  try {
    const response = await fetch('/atoll/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({ client_id: clientId, refresh_token: refreshToken }),
    });
    const data = await response.json();

    if (response.ok && data.access_token) {
      setJwtToken(data.access_token);
      if (data.refresh_token) sessionStorage.setItem('refresh_token', data.refresh_token);
      return {
        success: true,
        status: response.status,
        value: { outcome: true, content: { outcome: { token: data.access_token, payload: { rnw: data.refresh_token } } } },
        headers: response.headers,
      };
    }
    return {
      success: false,
      status: response.status,
      value: { outcome: false, error: data.error || `refresh ${response.status}` },
      headers: response.headers,
    };
  } catch (error) {
    return {
      success: false,
      status: 500,
      value: { outcome: false, error: error instanceof Error ? error.message : 'refresh failed' },
      headers: null,
    };
  }
};
